import React, { useRef, useEffect, useState, useCallback } from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { cn } from '../../utils/cn';
import { useVideoStore } from '../../stores/videoStore';
import { validateVideoFile } from './utils';
import VideoControls from './VideoControls';
import FrameControls from './FrameControls';
import VideoUploader from './VideoUploader';
import { debounce } from 'lodash';

const VideoPlayer: React.FC = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [sliderValue, setSliderValue] = useState(0);
  const lastTimeUpdateRef = useRef<number>(0);
  const isSeekingRef = useRef(false);
  const requestAnimationFrameRef = useRef<number>();
  const sliderUpdateTimeoutRef = useRef<NodeJS.Timeout>();

  const {
    videoUrl,
    currentFrame,
    currentTime,
    fps,
    duration,
    setVideoUrl,
    setCurrentFrame,
    setCurrentTime,
    setDuration,
    setFps,
    reset,
  } = useVideoStore();

  const updateVideoTime = useCallback((time: number) => {
    if (videoRef.current) {
      isSeekingRef.current = true;
      videoRef.current.currentTime = time;
      setCurrentTime(time);
      requestAnimationFrame(() => {
        isSeekingRef.current = false;
      });
    }
  }, [setCurrentTime]);

  const debouncedUpdateVideoTime = useCallback(
    debounce((time: number) => updateVideoTime(time), 16, { maxWait: 32 }),
    [updateVideoTime]
  );

  const handleFileSelect = async (file: File) => {
    try {
      setError(null);
      setIsLoading(true);
      setLoadingProgress(0);

      // Start loading progress monitoring
      const reader = new FileReader();
      reader.onprogress = (event) => {
        if (event.lengthComputable) {
          const progress = (event.loaded / event.total) * 100;
          setLoadingProgress(Math.round(progress));
        }
      };

      // Validate file and detect FPS
      const { valid, fps: detectedFps } = await validateVideoFile(file);
      if (!valid) {
        throw new Error('動画の検証に失敗しました。');
      }

      // Update FPS if detected
      if (detectedFps) {
        setFps(detectedFps);
      }

      // Create object URL and start loading
      const url = URL.createObjectURL(file);
      setVideoUrl(url);
      setSliderValue(0);
      setCurrentTime(0);
      setCurrentFrame(0);

      // Start reading the file to monitor progress
      reader.readAsArrayBuffer(file);

    } catch (err) {
      setError(err instanceof Error ? err.message : '動画の読み込みに失敗しました。');
      reset();
    } finally {
      setIsLoading(false);
    }
  };

  const handleVideoError = () => {
    setError('動画の再生に失敗しました。別の動画を試してください。');
    reset();
  };

  const handleLoadStart = () => {
    setIsLoading(true);
    setError(null);
  };

  const handleCanPlay = () => {
    setIsLoading(false);
    if (videoRef.current) {
      videoRef.current.currentTime = currentTime;
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
      videoRef.current.currentTime = currentTime;
    }
  };

  const handleReset = () => {
    setError(null);
    setIsLoading(false);
    setSliderValue(0);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.removeAttribute('src');
      videoRef.current.load();
    }
    reset();
  };

  const skipFrames = (frames: number) => {
    if (videoRef.current) {
      const newTime = videoRef.current.currentTime + (frames / fps);
      const clampedTime = Math.max(0, Math.min(newTime, duration));
      updateVideoTime(clampedTime);
      setSliderValue(clampedTime);
    }
  };

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = parseFloat(e.target.value);
    setSliderValue(time);

    if (sliderUpdateTimeoutRef.current) {
      clearTimeout(sliderUpdateTimeoutRef.current);
    }

    sliderUpdateTimeoutRef.current = setTimeout(() => {
      if (isDragging) {
        debouncedUpdateVideoTime(time);
      } else {
        updateVideoTime(time);
      }
    }, 16);
  };

  const handleSliderMouseDown = () => {
    setIsDragging(true);
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  const handleSliderMouseUp = () => {
    setIsDragging(false);
    updateVideoTime(sliderValue);
  };

  const handleTimeUpdate = useCallback(() => {
    if (videoRef.current && !isDragging && !isSeekingRef.current) {
      const time = videoRef.current.currentTime;
      const now = performance.now();
      
      if (now - lastTimeUpdateRef.current >= 16) {
        if (requestAnimationFrameRef.current) {
          cancelAnimationFrame(requestAnimationFrameRef.current);
        }
        
        requestAnimationFrameRef.current = requestAnimationFrame(() => {
          setCurrentTime(time);
          setSliderValue(time);
          lastTimeUpdateRef.current = now;
        });
      }
    }
  }, [isDragging, setCurrentTime]);

  const handleSeeking = () => {
    isSeekingRef.current = true;
  };

  const handleSeeked = () => {
    isSeekingRef.current = false;
  };

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.addEventListener('timeupdate', handleTimeUpdate);
      video.addEventListener('seeking', handleSeeking);
      video.addEventListener('seeked', handleSeeked);
      return () => {
        video.removeEventListener('timeupdate', handleTimeUpdate);
        video.removeEventListener('seeking', handleSeeking);
        video.removeEventListener('seeked', handleSeeked);
        if (requestAnimationFrameRef.current) {
          cancelAnimationFrame(requestAnimationFrameRef.current);
        }
        if (sliderUpdateTimeoutRef.current) {
          clearTimeout(sliderUpdateTimeoutRef.current);
        }
      };
    }
  }, [handleTimeUpdate]);

  return (
    <div className="w-full bg-gray-900 rounded-lg shadow-lg overflow-hidden">
      <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
        {!videoUrl && !error && !isLoading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <VideoUploader onFileSelect={handleFileSelect} />
          </div>
        )}

        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
            <div className="text-center p-4">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-2" />
              <div className="text-red-500 text-lg mb-4">{error}</div>
              <button
                onClick={handleReset}
                className="btn btn-primary"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                再試行
              </button>
            </div>
          </div>
        )}

        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
            <div className="text-center w-64">
              <div className="relative h-2 bg-gray-700 rounded-full overflow-hidden mb-2">
                <div 
                  className="absolute left-0 top-0 h-full bg-primary-500 transition-all duration-300"
                  style={{ width: `${loadingProgress}%` }}
                />
              </div>
              <div className="text-white text-sm">
                読み込み中... {loadingProgress}%
              </div>
            </div>
          </div>
        )}

        <video
          ref={videoRef}
          className={cn(
            "absolute inset-0 w-full h-full object-contain bg-black touch-none",
            (!videoUrl || error || isLoading) && "hidden"
          )}
          onLoadedMetadata={handleLoadedMetadata}
          onError={handleVideoError}
          onLoadStart={handleLoadStart}
          onCanPlay={handleCanPlay}
          playsInline
          preload="metadata"
          src={videoUrl || undefined}
          controlsList="nodownload nofullscreen noremoteplayback"
        />
      </div>

      <div className="bg-gray-800 p-4 space-y-4">
        <VideoControls
          currentTime={currentTime}
          duration={duration}
          currentFrame={currentFrame}
          hasError={!!error}
          onReset={handleReset}
        />

        <input
          type="range"
          min={0}
          max={duration}
          step={1 / fps}
          value={sliderValue}
          onChange={handleSliderChange}
          onMouseDown={handleSliderMouseDown}
          onMouseUp={handleSliderMouseUp}
          onTouchStart={handleSliderMouseDown}
          onTouchEnd={handleSliderMouseUp}
          className="w-full touch-manipulation appearance-none h-2 rounded-lg bg-gray-700 cursor-pointer"
          style={{
            background: `linear-gradient(to right, #ef4444 ${(sliderValue / duration) * 100}%, #374151 ${(sliderValue / duration) * 100}%)`,
          }}
          disabled={!videoUrl || isLoading || !!error}
        />

        <style>{`
          input[type=range]::-webkit-slider-thumb {
            -webkit-appearance: none;
            appearance: none;
            width: 16px;
            height: 16px;
            background: #ef4444;
            border-radius: 50%;
            cursor: pointer;
            transition: all 0.15s ease;
          }

          input[type=range]::-webkit-slider-thumb:hover {
            transform: scale(1.2);
          }

          input[type=range]::-moz-range-thumb {
            width: 16px;
            height: 16px;
            background: #ef4444;
            border-radius: 50%;
            cursor: pointer;
            border: none;
            transition: all 0.15s ease;
          }

          input[type=range]::-moz-range-thumb:hover {
            transform: scale(1.2);
          }
        `}</style>

        <FrameControls
          onSkipFrames={skipFrames}
          disabled={!videoUrl || isLoading || !!error}
        />
      </div>
    </div>
  );
};

export default VideoPlayer;